import React, { useState, useEffect } from 'react';
import { Users, MessageCircle, Zap, Award, Share2, Key } from 'lucide-react';

const SpacesSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [radius, setRadius] = useState(200);

  const items = [
    { icon: Users, label: 'Connect' },
    { icon: MessageCircle, label: 'Collaborate' },
    { icon: Zap, label: 'Engage' },
    { icon: Award, label: 'Reward' },
    { icon: Share2, label: 'Share' },
    { icon: Key, label: 'Own' },
  ];

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 400) {
        setRadius(110);
      } else if (window.innerWidth < 640) {
        setRadius(130);
      } else if (window.innerWidth < 768) {
        setRadius(150);
      } else {
        setRadius(200);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [items.length]);

  return (
    <section className="relative bg-white pt-8 pb-24 md:pt-12 md:pb-32 overflow-hidden">
      {/* Decorative connecting element */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-16 bg-gradient-to-b from-gray-200 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Side - Content */}
          <div className="space-y-6 md:space-y-8 order-2 lg:order-1">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Powering{' '}
              <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                InteractionFi
              </span>
            </h2>

            <p className="text-lg md:text-xl text-gray-600 mb-8">
              from live sessions to collaborative workspaces.
            </p>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4 md:gap-8 pt-6 md:pt-8 border-t border-gray-200">
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-1 md:mb-2">Zero friction</h3>
                <p className="text-sm md:text-base text-gray-600">No wallets required</p>
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-1 md:mb-2">Full control</h3>
                <p className="text-sm md:text-base text-gray-600">User-owned data</p>
              </div>
            </div>
          </div>

          {/* Right Side - Circular Diagram */}
          <div className="relative flex items-center justify-center order-1 lg:order-2 lg:pl-12 lg:border-l-2 lg:border-gray-200">
            <div className="relative w-full max-w-[320px] sm:max-w-[380px] md:max-w-[500px] aspect-square mx-auto">
              {/* Center Circle - Spaces */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 bg-black rounded-full flex flex-col items-center justify-center shadow-xl z-10">
                <div className="text-white text-lg sm:text-xl md:text-2xl font-bold tracking-wide">Spaces</div>
              </div>

              {/* Static Container - No rotation */}
              <div className="absolute inset-0">
                {/* Items positioned in circle - Static positions */}
                {items.map((item, index) => {
                  const Icon = item.icon;
                  const angle = (index * 60 - 90) * (Math.PI / 180); // -90 to start at top
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;
                  const isActive = index === activeIndex;

                  return (
                    <div
                      key={index}
                      className="absolute top-1/2 left-1/2"
                      style={{
                        transform: `translate(${x}px, ${y}px) translate(-50%, -50%)`,
                      }}
                    >
                      <div
                        className={`bg-white border-2 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl flex flex-col items-center justify-center transition-all duration-700 ${
                          isActive 
                            ? 'scale-110 shadow-xl border-gray-900 bg-gray-50' 
                            : 'scale-100 shadow-md border-gray-300'
                        }`}
                      >
                        <Icon 
                          className={`transition-all duration-700 ${
                            isActive ? 'w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-gray-900' : 'w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-600'
                          }`} 
                          strokeWidth={2}
                        />
                        <span className={`text-[10px] sm:text-xs mt-1 sm:mt-1.5 font-medium transition-all duration-700 ${
                          isActive ? 'opacity-100 text-gray-900 font-semibold' : 'opacity-80 text-gray-600'
                        }`}>
                          {item.label}
                        </span>
                      </div>

                      {/* Pulse effect for active item */}
                      {isActive && (
                        <div className="absolute inset-0 rounded-xl border-2 border-gray-900 animate-ping opacity-20"></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpacesSection;
